/** Debug sheet: mock GPS / heading, simulation, raw values, horizon line. */
import type { DebugSettings, UiCallbacks } from '../contracts';
import { debugStore } from '../settings';
import { type Control, section, slider, toggle } from './controls';
import { button, h, setHidden } from './dom';
import { formatLatLon, parseLatLon } from './format';
import { icon } from './icons';
import { Sheet } from './sheet';

export interface MockPreset {
  id: string;
  label: string;
  lat: number;
  lon: number;
}

/** Checked against the official data: zone as noted. */
export const MOCK_PRESETS: MockPreset[] = [
  { id: 'lu-inner', label: 'Luzern Seebecken · innere Zone', lat: 47.0535, lon: 8.3155 },
  { id: 'lu-open', label: 'Luzern Seebecken · offen', lat: 47.041, lon: 8.339 },
  { id: 'horw', label: 'Horwer Bucht · erweitert', lat: 47.007, lon: 8.313 },
  { id: 'weggis', label: 'Weggis · äussere Zone', lat: 47.029, lon: 8.435 },
  { id: 'sempach', label: 'Sempachersee · Mitte', lat: 47.145, lon: 8.155 },
];

const NUDGE_M = 10;

export interface DebugDeps {
  callbacks: UiCallbacks;
  toast(text: string, kind?: 'info' | 'warn' | 'error'): void;
  backToSettings(): void;
  isSession(): boolean;
}

export class DebugPanel {
  readonly sheet: Sheet;
  private readonly controls: Control[] = [];
  private readonly unsubs: (() => void)[] = [];

  constructor(deps: DebugDeps) {
    this.sheet = new Sheet('Debug', { onBack: () => deps.backToSettings(), backLabel: 'Zurück zu den Einstellungen' });
    const d = () => debugStore.get();
    const set = (patch: Partial<DebugSettings>) => debugStore.update(patch);

    const note = h('p', 'uz-note', null, [
      'Texteingaben am besten auf dem Startbildschirm bearbeiten: Die Tastatur verdeckt die AR-Ansicht.',
    ]);
    const noteSync: Control = { el: note, sync: () => setHidden(note, !deps.isSession()) };

    // Mock GPS
    const mockGps = toggle({ label: 'Mock-GPS', sub: 'Feste Position statt GPS', get: () => d().mockGps, set: (v) => set({ mockGps: v }) });
    const pos = h('div', 'uz-mockpos');
    const presetBtns = MOCK_PRESETS.map((p) => {
      const b = button('uz-preset', [p.label, h('small', null, null, [formatLatLon(p)])], () => {
        set({ mockLat: p.lat, mockLon: p.lon, mockGps: true });
        deps.toast(`Mock-Position: ${p.label}`);
      });
      b.dataset.lat = String(p.lat);
      b.dataset.lon = String(p.lon);
      return b;
    });
    const presets = h('div', 'uz-presets', { role: 'group', 'aria-label': 'Mock-Positionen' }, presetBtns);
    const input = h('input', 'uz-input', {
      type: 'text',
      inputmode: 'decimal',
      autocomplete: 'off',
      spellcheck: 'false',
      placeholder: '47.05, 8.31',
      'aria-label': 'Koordinaten (Breite, Länge)',
    });
    const apply = () => {
      const p = parseLatLon(input.value);
      if (!p) {
        deps.toast('Ungültige Koordinaten. Format: 47.05, 8.31', 'warn');
        return;
      }
      set({ mockLat: p.lat, mockLon: p.lon, mockGps: true });
      input.value = '';
      input.blur();
      deps.toast(`Mock-Position: ${formatLatLon(p)}`);
    };
    input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') apply();
    });
    const paste = h('div', 'uz-paste', null, [input, button('uz-btn uz-btn--light', 'Setzen', apply)]);
    const posSync: Control = {
      el: pos,
      sync: () => {
        const v = d();
        pos.textContent = `Position: ${formatLatLon({ lat: v.mockLat, lon: v.mockLon })}`;
        for (const b of presetBtns) {
          const on = Math.abs(Number(b.dataset.lat) - v.mockLat) < 1e-7 && Math.abs(Number(b.dataset.lon) - v.mockLon) < 1e-7;
          b.setAttribute('aria-pressed', String(on));
        }
      },
    };

    const acc = slider({
      label: 'Mock-Genauigkeit',
      min: 1,
      max: 30,
      step: 0.5,
      get: () => d().mockAccuracyM,
      set: (v) => set({ mockAccuracyM: v }),
      format: (v) => `±${v.toFixed(1)} m`,
      scale: ['1 m', '30 m'],
    });
    const speed = slider({
      label: 'Mock-Geschwindigkeit',
      min: 0,
      max: 30,
      step: 0.5,
      get: () => d().mockSpeedKmh,
      set: (v) => set({ mockSpeedKmh: v }),
      format: (v) => `${v.toFixed(1)} km/h`,
      scale: ['0', '30 km/h'],
    });

    const nudge = (e: number, n: number, label: string) =>
      button('uz-btn', label, () => deps.callbacks.onMockNudge(e, n), { 'aria-label': `${NUDGE_M} m nach ${label}` });
    const dpad = h('div', 'uz-dpad', { role: 'group', 'aria-label': 'Mock-Position verschieben' }, [
      h('span'),
      nudge(0, NUDGE_M, 'N'),
      h('span'),
      nudge(-NUDGE_M, 0, 'W'),
      h('span', 'uz-dpad-mid', null, [`${NUDGE_M} m`]),
      nudge(NUDGE_M, 0, 'O'),
      h('span'),
      nudge(0, -NUDGE_M, 'S'),
      h('span'),
    ]);

    // Mock heading
    const mockHead = toggle({ label: 'Mock-Kompass', sub: 'Feste Blickrichtung', get: () => d().mockHeading, set: (v) => set({ mockHeading: v }) });
    const head = slider({
      label: 'Mock-Richtung',
      min: 0,
      max: 359,
      step: 1,
      get: () => d().mockHeadingDeg,
      set: (v) => set({ mockHeadingDeg: v }),
      format: (v) => `${Math.round(v)}°`,
      scale: ['0° N', '359°'],
    });
    const fov = slider({
      label: 'Kamera-Sichtfeld',
      min: 40,
      max: 90,
      step: 1,
      get: () => d().cameraVFovDeg,
      set: (v) => set({ cameraVFovDeg: v }),
      format: (v) => `${Math.round(v)}°`,
      scale: ['40°', '90°'],
    });

    const sim = button('uz-btn uz-btn--primary uz-btn--block', [icon('play'), 'Simulation starten'], () => deps.callbacks.onAction('start-sim'));
    const raw = toggle({ label: 'Rohwerte anzeigen', sub: 'Tabelle im HUD', get: () => d().showRaw, set: (v) => set({ showRaw: v }) });
    const horizon = toggle({ label: 'Horizontlinie', get: () => d().showHorizon, set: (v) => set({ showHorizon: v }) });
    const off = button('uz-btn uz-btn--block', 'Debug-Modus beenden', () => {
      set({ enabled: false });
      deps.toast('Debug-Modus deaktiviert');
      this.sheet.close();
    });

    this.controls.push(noteSync, mockGps, posSync, acc, speed, mockHead, head, fov, raw, horizon);

    this.sheet.body.append(
      note,
      section('Position', [mockGps.el, pos, presets, paste]),
      section('Mock-Genauigkeit', [acc.el], { value: acc.valueEl }),
      section('Mock-Geschwindigkeit', [speed.el], { value: speed.valueEl, help: 'Bewegt die Mock-Position in Blickrichtung.' }),
      section('Verschieben', [dpad]),
      section('Mock-Kompass', [mockHead.el, head.el], { value: head.valueEl }),
      section('Kamera-Sichtfeld (Simulation)', [fov.el], { value: fov.valueEl }),
      section('Simulation', [sim], { help: 'Ohne Kamera: Hintergrund-Verlauf, Blickrichtung per Mock-Kompass und Ziehen.' }),
      section('Anzeige', [raw.el, horizon.el]),
      section('Debug', [off]),
    );

    const syncAll = () => this.controls.forEach((c) => c.sync());
    this.unsubs.push(debugStore.subscribe(syncAll));
    syncAll();
  }

  dispose(): void {
    this.unsubs.forEach((u) => u());
  }

  open(opener?: HTMLElement | null): void {
    this.controls[0].sync(); // session hint
    this.sheet.open(opener);
  }
}
